/**
 * Employee Management Service
 * Handles all employee management operations including listing, updating, and role/department assignment
 */

import { apiGet, apiPost, apiPut, apiPatch, apiPostFormData } from '@/shared/api/apiClient';

/**
 * Employee List Item DTO
 */
export interface EmployeeListItem {
  id: string;
  tenantId?: string;
  employeeCode: string;
  fullName: string;
  email: string;
  jobTitle: string;
  departmentId?: string | null;
  departmentName?: string;
  roleName?: string;
  statusId: string;
  statusValue: string;
  joiningDate: string;
  employmentTypeId: string;
  employmentTypeValue: string;
  managerId?: string | null;
  needsProbationConfirmation?: boolean;
}

/**
 * Paginated result
 */
export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/**
 * Query parameters for getting employees
 */
export interface GetEmployeesParams {
  tenantId: string;
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  departmentId?: string;
  /** common.system_codes RowId, Key="EmployeeStatus". */
  statusId?: string;
  roleId?: string;
  sortBy?: string;
  sortOrder?: string;
}

/**
 * Update employee job info request
 */
export interface UpdateEmployeeJobInfoRequest {
  employeeId: string;
  /** Omit or leave blank to keep the existing code. Must be unique within the tenant. */
  employeeCode?: string;
  jobTitle: string;
  departmentId: string | null;
  roleId?: string;
  designationId?: string;
  workShiftId?: string;
  managerId?: string | null;
  employmentTypeId: string;
  joiningDate?: string;
}

/**
 * Update employee personal info request
 */
export interface UpdateEmployeePersonalInfoRequest {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  maritalStatusId: string;
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

/**
 * Update employee salary request
 */
export interface UpdateEmployeeSalaryRequest {
  employeeId: string;
  newSalary: number;
  currency: string;
  reason: string;
}

/**
 * Create employee request
 */
export interface CreateEmployeeRequest {
  // Mandatory fields
  firstName: string;
  lastName: string;
  email: string;
  
  // Optional fields
  employeeCode?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  genderId?: string;
  maritalStatusId?: string;
  password?: string;
  
  // Address (optional)
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  
  // Job information (optional)
  jobTitle?: string;
  departmentId?: string;
  roleId?: string;
  designationId?: string;
  workShiftId?: string;
  managerId?: string;
  joiningDate?: string;
  employmentTypeId?: string;

  // Compensation (optional)
  salary?: number;
  currency?: string;
}

/**
 * Get all employees with pagination and filters
 */
export async function getAllEmployees(
  params: GetEmployeesParams
): Promise<PagedResult<EmployeeListItem>> {
  const queryParams = new URLSearchParams();
  
  queryParams.append('tenantId', params.tenantId);
  queryParams.append('pageNumber', (params.pageNumber || 1).toString());
  queryParams.append('pageSize', (params.pageSize || 10).toString());
  
  if (params.searchTerm) {
    queryParams.append('searchTerm', params.searchTerm);
  }
  
  if (params.departmentId) {
    queryParams.append('departmentId', params.departmentId);
  }
  
  if (params.statusId) {
    queryParams.append('statusId', params.statusId);
  }

  if (params.roleId) {
    queryParams.append('roleId', params.roleId);
  }

  if (params.sortBy) {
    queryParams.append('sortBy', params.sortBy);
  }
  
  if (params.sortOrder) {
    queryParams.append('sortOrder', params.sortOrder);
  }

  const endpoint = `/employees?${queryParams.toString()}`;

  return apiGet<PagedResult<EmployeeListItem>>(endpoint);
}

/**
 * Update employee job information
 */
export async function updateEmployeeJobInfo(
  employeeId: string,
  tenantId: string,
  data: UpdateEmployeeJobInfoRequest
): Promise<void> {
  return apiPut<void>(`/employees/${employeeId}/job-info?tenantId=${tenantId}`, data);
}

/**
 * Update employee personal information
 */
export async function updateEmployeePersonalInfo(
  employeeId: string,
  data: UpdateEmployeePersonalInfoRequest,
  tenantId?: string
): Promise<void> {
  const url = tenantId
    ? `/employees/${employeeId}/personal-info?tenantId=${tenantId}`
    : `/employees/${employeeId}/personal-info`;
  return apiPut<void>(url, data);
}

/**
 * Update employee salary
 */
export async function updateEmployeeSalary(
  employeeId: string,
  tenantId: string,
  data: UpdateEmployeeSalaryRequest
): Promise<void> {
  return apiPut<void>(`/employees/${employeeId}/salary?tenantId=${tenantId}`, data);
}

/**
 * Create a new employee
 */
export async function createEmployee(
  tenantId: string,
  userId: string,
  data: CreateEmployeeRequest
): Promise<string> {
  const result = await apiPost<{ id: string }>(`/employees?tenantId=${tenantId}&userId=${userId}`, data);
  return result.id;
}

/**
 * Complete probation for an employee.
 * Calls PATCH /api/v1/probation/complete/{employeeId}
 * Backend zeros PROB balance and grants prorated EL quota for remaining months.
 */
export async function completeProbation(employeeId: string): Promise<void> {
  await apiPatch<unknown>(`/probation/complete/${employeeId}`);
}

/**
 * Update employee status (Active, Inactive, Resigned, Retired, Notice, OnLeave).
 * `newStatusId` is a common.system_codes RowId, Key="EmployeeStatus" (from useSystemCodes).
 * Calls PATCH /api/v1/employees/{employeeId}/status
 */
export async function updateEmployeeStatus(
  employeeId: string,
  newStatusId: string
): Promise<void> {
  await apiPatch<unknown>(`/employees/${employeeId}/status`, { newStatusId });
}

export interface JobHistoryEntry {
  id: string;
  employeeId: string;
  jobTitle: string;
  departmentId?: string;
  departmentName?: string;
  managerId?: string;
  managerName?: string;
  effectiveFrom: string;
}

export async function getEmployeeJobHistory(employeeId: string): Promise<JobHistoryEntry[]> {
  return apiGet<JobHistoryEntry[]>(`/employees/${employeeId}/job-history`);
}

// ─── Bulk Import ──────────────────────────────────────────────────────────────

export interface BulkImportResult {
  totalRows: number;
  imported: number;
  skipped: number;
  warnings: string[];
  errors: string[];
}

/**
 * Upload an Excel file and bulk-import employees.
 * @param file  The .xlsx file selected by the user.
 * @param tenantId  The tenant scope for the import.
 */
export async function importEmployeesFromExcel(
  file: File,
  tenantId: string,
): Promise<BulkImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  return apiPostFormData<BulkImportResult>(`/employees/import?tenantId=${tenantId}`, formData);
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk modification & teams
//
// A "team" is not a stored entity — it is a manager plus everyone whose managerId points at
// them, nested recursively. Assigning one manager to several employees at once is therefore
// both a bulk edit and a team formation.
// ─────────────────────────────────────────────────────────────────────────────

/** Server-side re-resolution of the grid's filters, for "select all N matching". */
export interface BulkTargetFilter {
  departmentId?: string | null;
  /** common.system_codes RowId, Key="EmployeeStatus". */
  statusId?: string | null;
  roleId?: string | null;
  searchTerm?: string | null;
  /** Rows the user un-ticked after choosing "select all matching". */
  excludedEmployeeIds?: string[];
}

/**
 * Each field is opt-in via its `set*` flag — that is what separates "leave unchanged" from
 * "clear to null". Supply `employeeIds` or `filter`, never both.
 */
export interface BulkUpdateJobInfoRequest {
  employeeIds?: string[];
  filter?: BulkTargetFilter;
  setJobTitle?: boolean;
  jobTitle?: string | null;
  setDepartment?: boolean;
  departmentId?: string | null;
  setManager?: boolean;
  managerId?: string | null;
}

export type BulkItemOutcome = 'Updated' | 'Unchanged' | 'Skipped';

export interface BulkJobUpdateItem {
  employeeId: string;
  employeeCode: string;
  fullName: string;
  outcome: BulkItemOutcome;
  message?: string;
}

export interface BulkJobUpdateResult {
  dryRun: boolean;
  totalTargeted: number;
  updated: number;
  unchanged: number;
  skipped: number;
  items: BulkJobUpdateItem[];
  warnings: string[];
  errors: string[];
}

export interface ManagerOption {
  id: string;
  fullName: string;
  employeeCode: string;
  email: string;
  jobTitle?: string;
  departmentId?: string | null;
  departmentName?: string;
  directReportCount: number;
}

export interface JobTitleSuggestion {
  title: string;
  usageCount: number;
  isDesignation: boolean;
}

export interface TeamNode {
  id: string;
  fullName: string;
  employeeCode: string;
  email: string;
  jobTitle?: string;
  departmentId?: string | null;
  departmentName?: string;
  managerId?: string | null;
  depth: number;
  directReportCount: number;
  /** Everyone below this node within the requested depth. Only meaningful on the root. */
  totalTeamSize: number;
  reports: TeamNode[];
  /** Organisation tree only: not currently active (shown muted). */
  isInactive?: boolean;
  /** Organisation tree only: a grouping card rather than a person (fullName holds its title). */
  kind?: 'organisation' | 'department';
}

/**
 * POST /api/employees/bulk/job-info/preview — reports what would change, writes nothing.
 * Tenant comes from the X-Tenant-Id header the api client attaches; bulk writes deliberately
 * ignore a ?tenantId= override.
 */
export const previewBulkEmployeeJobInfo = (
  request: BulkUpdateJobInfoRequest,
): Promise<BulkJobUpdateResult> =>
  apiPost<BulkJobUpdateResult>('/employees/bulk/job-info/preview', request);

/** POST /api/employees/bulk/job-info — applies the change. */
export const bulkUpdateEmployeeJobInfo = (
  request: BulkUpdateJobInfoRequest,
): Promise<BulkJobUpdateResult> =>
  apiPost<BulkJobUpdateResult>('/employees/bulk/job-info', request);

/** GET /api/employees/managers — candidate managers, existing managers first. */
export const getManagerOptions = (search?: string, take = 50): Promise<ManagerOption[]> => {
  const query = new URLSearchParams({ take: String(take) });
  if (search?.trim()) query.set('search', search.trim());
  return apiGet<ManagerOption[]>(`/employees/managers?${query}`);
};

/** GET /api/employees/job-title-suggestions — titles in use plus active designations. */
export const getJobTitleSuggestions = (search?: string, take = 50): Promise<JobTitleSuggestion[]> => {
  const query = new URLSearchParams({ take: String(take) });
  if (search?.trim()) query.set('search', search.trim());
  return apiGet<JobTitleSuggestion[]>(`/employees/job-title-suggestions?${query}`);
};

/** GET /api/employees/{id}/team — the manager's nested team tree. */
export const getTeamTree = (managerId: string, maxDepth = 3): Promise<TeamNode> =>
  apiGet<TeamNode>(`/employees/${managerId}/team?maxDepth=${maxDepth}`);

/** An employee's place in the organisation: their manager on top (null at the top), then their team. */
export interface ReportingTree {
  /** A single node: no reports loaded, directReportCount set. */
  manager: TeamNode | null;
  root: TeamNode;
}

/**
 * GET /api/employees/me/reporting-tree, or /employees/{id}/reporting-tree for someone else
 * (Admin/Director, or someone in the caller's reporting line — the server decides).
 */
export const getReportingTree = (employeeId?: string, maxDepth = 3): Promise<ReportingTree> =>
  apiGet<ReportingTree>(`/employees/${employeeId ?? 'me'}/reporting-tree?maxDepth=${maxDepth}`);

/** One employee in the org tree. Flat — the screen groups by department and nests by managerId. */
export interface OrgTreeEmployee {
  id: string;
  fullName: string;
  employeeCode: string;
  email: string;
  jobTitle?: string;
  departmentId?: string | null;
  departmentName?: string | null;
  managerId?: string | null;
  managerName?: string | null;
  statusId: string;
  statusValue: string;
}

/** GET /api/employees/org-tree — every employee with their department and manager. */
export const getOrgTree = (
  departmentId?: string,
  includeTerminated = false,
): Promise<OrgTreeEmployee[]> => {
  const query = new URLSearchParams({ includeTerminated: String(includeTerminated) });
  if (departmentId) query.set('departmentId', departmentId);
  return apiGet<OrgTreeEmployee[]>(`/employees/org-tree?${query}`);
};
